import React from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import ArrowRightLineIcon from "@rsuite/icons/ArrowRightLine";
import VisibleIcon from "@rsuite/icons/Visible";
import UnvisibleIcon from "@rsuite/icons/Unvisible";
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import { GlassCard, Button, Badge, NoDataState } from "../../../../ui";

export const SystemListFC = ({ systems, acuamticaSystems }) => {
  const history = useHistory();

  const getAcumaticaStatus = (system) => {
    if (system.descrepency === true) return "warning";
    if (system.descrepency === false) return "success";
    return "error";
  };

  const getAcumaticaText = (system) => {
    if (system.descrepency === true) return "Discrepancies";
    if (system.descrepency === false) return "Synced";
    return "Not Found";
  };

  const getModalityColor = (modality) => {
    const colors = {
      MRI: "primary",
      CT: "info",
      US: "success",
      DR: "warning",
    };
    return colors[modality] || "neutral";
  };

  if (!systems || systems.length === 0) {
    return (
      <NoDataState
        title="No Systems Found"
        description="No systems match the current filters"
      />
    );
  }

  return (
    <SystemListContainer>
      <SystemGrid>
        {systems.map((system, index) => (
          <GlassCard
            key={system.id}
            animated
            delay={index * 50}
            hoverable
            onClick={() => history.push(`/admin/system/${system.id}`)}
          >
            <SystemCard>
              <SystemHeader>
                <SystemTitle>
                  <SystemId>{system.id}</SystemId>
                  <StatusIcons>
                    {system.show_on_website ? (
                      <StatusIcon title="Visible to Clients">
                        <VisibleIcon color="green" />
                      </StatusIcon>
                    ) : (
                      <StatusIcon title="Not Visible to Clients">
                        <UnvisibleIcon color="red" />
                      </StatusIcon>
                    )}
                    <AcumaticaBadge variant={getAcumaticaStatus(system)}>
                      {getAcumaticaText(system)}
                    </AcumaticaBadge>
                  </StatusIcons>
                </SystemTitle>
              </SystemHeader>

              <SystemBody>
                <SystemInfo>
                  <InfoSection>
                    <InfoLabel>Equipment</InfoLabel>
                    <InfoValue>{system.manufacturer}</InfoValue>
                    <InfoSubValue>{system.model}</InfoSubValue>
                  </InfoSection>

                  <InfoSection>
                    <InfoLabel>Modality</InfoLabel>
                    <ModalityBadge variant={getModalityColor(system.modality)}>
                      {system.modality}
                    </ModalityBadge>
                  </InfoSection>

                  <InfoSection>
                    <InfoLabel>Customer</InfoLabel>
                    <InfoValue>{system.customer_name}</InfoValue>
                    <InfoSubValue>ID: {system.customer_id}</InfoSubValue>
                  </InfoSection>

                  <InfoSection>
                    <InfoLabel>Site</InfoLabel>
                    <InfoValue>{system.site_name}</InfoValue>
                    <InfoSubValue>ID: {system.site_id}</InfoSubValue>
                  </InfoSection>
                </SystemInfo>

                <ProcessingStatus>
                  <ProcessLabel>Processing Status</ProcessLabel>
                  <ProcessIcons>
                    <ProcessItem>
                      <ProcessIcon enabled={system.process_edu}>
                        <CheckRoundIcon
                          color={system.process_edu ? "green" : "grey"}
                        />
                      </ProcessIcon>
                      <ProcessText>EDU</ProcessText>
                    </ProcessItem>
                    <ProcessItem>
                      <ProcessIcon enabled={system.process_mag}>
                        <CheckRoundIcon
                          color={system.process_mag ? "green" : "grey"}
                        />
                      </ProcessIcon>
                      <ProcessText>MAG</ProcessText>
                    </ProcessItem>
                    <ProcessItem>
                      <ProcessIcon enabled={system.process_log}>
                        <CheckRoundIcon
                          color={system.process_log ? "green" : "grey"}
                        />
                      </ProcessIcon>
                      <ProcessText>LOG</ProcessText>
                    </ProcessItem>
                  </ProcessIcons>
                </ProcessingStatus>
              </SystemBody>

              <SystemFooter>
                <Button
                  variant="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/admin/system/${system.id}`);
                  }}
                >
                  View Details
                  <ArrowRightLineIcon style={{ marginLeft: "0.5rem" }} />
                </Button>
              </SystemFooter>
            </SystemCard>
          </GlassCard>
        ))}
      </SystemGrid>
    </SystemListContainer>
  );
};

// Styled Components
const SystemListContainer = styled.div`
  width: 100%;
`;

const SystemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SystemCard = styled.div`
  padding: 1.5rem;
  transition: all 0.3s ease;
  min-height: 280px;
  display: flex;
  flex-direction: column;
`;

const SystemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SystemTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const SystemId = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  cursor: help;
`;

const AcumaticaBadge = styled(Badge)`
  font-size: 0.75rem;
`;

const ModalityBadge = styled(Badge)`
  font-weight: 600;
`;

const SystemBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SystemInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.6);
`;

const InfoValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.9);
  line-height: 1.2;
`;

const InfoSubValue = styled.div`
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.2;
`;

const ProcessingStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProcessLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.6);
`;

const ProcessIcons = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const ProcessItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const ProcessIcon = styled.div`
  opacity: ${({ enabled }) => (enabled ? 1 : 0.5)};
`;

const ProcessText = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
`;

const SystemFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  margin-top: auto;
`;
